import { useEffect, useRef } from 'react';
import { type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useConversations } from './useChat';
import { tokenManager } from '@/lib/api';
import { createAppSocket } from '@/lib/socket';

/**
 * Hook that returns the total unread message count for the current user.
 * Maintains a Socket.IO connection to receive real-time unread updates.
 * Pass enabled=false (e.g. admin roles, whose header hides the chat button)
 * to skip both the conversations fetch and the dedicated socket.
 */
export function useUnreadMessages(enabled = true): number {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const { data: conversations = [] } = useConversations(enabled);

  // Connect socket for real-time unread notifications
  useEffect(() => {
    if (!enabled) return;
    const token = tokenManager.getAccessToken();
    if (!token) return;

    const socket = createAppSocket(token);

    socketRef.current = socket;

    socket.on('chat:unread', () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    });

    socket.on('chat:message', () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    });

    socket.on('chat:status-update', () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [queryClient, enabled]);

  if (!user) return 0;

  const isProfessional = user.role === 'professional';

  return conversations.reduce((total, conv) => {
    const unread = isProfessional ? conv.unreadCountProfessional : conv.unreadCountUser;
    return total + (unread || 0);
  }, 0);
}
